import { useState } from 'react';
import type { TFunction } from 'i18next';
import { AlertCircle, Copy, Plus } from 'lucide-react';
import { useAuthSession, useTranslation } from '@dukanti/shared';
import type { Courier, CourierErrorKey, TranslationKey } from '@dukanti/shared';
import { buildCourierPortalWhatsAppLink } from '@dukanti/shared/services';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useStore } from '@/features/store-settings/use-store';
import { useCurrentStore } from '@/hooks/use-current-store';
import { useCourierPermissions } from '@/hooks/use-courier-permissions';
import { useCouriers } from '@/hooks/use-couriers';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { useCreateCourier } from '@/hooks/use-create-courier';
import { useGetOrCreateCourierPortalLink } from '@/hooks/use-get-or-create-courier-portal-link';
import { useRegenerateCourierPortalLink } from '@/hooks/use-regenerate-courier-portal-link';
import { useRevokeCourierPortalLink } from '@/hooks/use-revoke-courier-portal-link';
import { useUpdateCourier } from '@/hooks/use-update-courier';
import { useSoftDeleteCourier } from '@/hooks/use-soft-delete-courier';
import {
  CourierFormDialog,
  CourierVerificationDialog,
  CouriersMobileCards,
  CouriersTable,
  DeleteCourierDialog,
  type CourierFormSubmit,
} from '@/features/couriers';

type FormMode = { readonly kind: 'create' } | { readonly kind: 'edit'; readonly courier: Courier };
type PortalLinkRecovery = {
  readonly courierName: string;
  readonly portalUrl: string;
};

function isShareAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export function CouriersRoute(): React.JSX.Element {
  const { t, dir, lang } = useTranslation();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const storeQuery = useStore(storeId);
  const permissionsQuery = useCourierPermissions(storeId);
  const canManageCouriers = permissionsQuery.data?.canManageCouriers === true;
  const couriersQuery = useCouriers(storeId, canManageCouriers);
  const { copy, isCopying } = useCopyToClipboard();

  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Courier | null>(null);
  const [verificationTarget, setVerificationTarget] = useState<Courier | null>(null);
  const [portalLinkRecovery, setPortalLinkRecovery] = useState<PortalLinkRecovery | null>(null);
  const [formError, setFormError] = useState<CourierErrorKey | null>(null);
  const [deleteError, setDeleteError] = useState<CourierErrorKey | null>(null);

  const createCourier = useCreateCourier(storeId ?? '');
  const updateCourier = useUpdateCourier(storeId ?? '');
  const deleteCourier = useSoftDeleteCourier(storeId ?? '');
  const getOrCreatePortalLink = useGetOrCreateCourierPortalLink(storeId ?? '');
  const revokePortalLink = useRevokeCourierPortalLink(storeId ?? '');
  const regeneratePortalLink = useRegenerateCourierPortalLink(storeId ?? '');

  const isLoading =
    permissionsQuery.isLoading || (canManageCouriers && (couriersQuery.isLoading || !storeId));
  const isMutating = createCourier.isPending || updateCourier.isPending || deleteCourier.isPending;
  const isPortalActionPending =
    getOrCreatePortalLink.isPending ||
    revokePortalLink.isPending ||
    regeneratePortalLink.isPending ||
    isCopying;
  const storeName = storeQuery.data?.name ?? t('admin.nav.storeFallback' as TranslationKey);

  async function resolvePortalUrl(courier: Courier): Promise<string | null> {
    if (!storeId || !courier.active || courier.verification_status !== 'approved') return null;
    const result = await getOrCreatePortalLink.mutateAsync(courier.id).catch(() => null);
    if (!result) return null;
    if (!result.success || !result.portal_url) {
      return null;
    }
    return result.portal_url;
  }

  async function handleCopyPortalLink(courier: Courier): Promise<void> {
    const portalUrl = await resolvePortalUrl(courier);
    if (!portalUrl) return;
    const copied = await copy(portalUrl);
    if (!copied) {
      setPortalLinkRecovery({ courierName: courier.name, portalUrl });
      return;
    }
    toast.success(t('admin.couriers.portal.copied' as TranslationKey));
  }

  async function handleSharePortalLink(courier: Courier): Promise<void> {
    const portalUrl = await resolvePortalUrl(courier);
    if (!portalUrl) return;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: t('admin.couriers.portal.copyLink' as TranslationKey),
          url: portalUrl,
        });
        return;
      } catch (error) {
        if (isShareAbort(error)) return;
        setPortalLinkRecovery({ courierName: courier.name, portalUrl });
        return;
      }
    }
    const copied = await copy(portalUrl);
    if (!copied) {
      setPortalLinkRecovery({ courierName: courier.name, portalUrl });
      return;
    }
    toast.success(t('admin.couriers.portal.copied' as TranslationKey));
  }

  async function handleCopyRecoveredPortalLink(): Promise<void> {
    if (!portalLinkRecovery) return;
    const copied = await copy(portalLinkRecovery.portalUrl);
    if (copied) {
      toast.success(t('admin.couriers.portal.copied' as TranslationKey));
      setPortalLinkRecovery(null);
      return;
    }
    toast.error(t('admin.couriers.portal.errors.copyFailed' as TranslationKey));
  }

  async function handleSendPortalWhatsApp(courier: Courier): Promise<void> {
    const portalUrl = await resolvePortalUrl(courier);
    if (!portalUrl) return;
    const whatsappUrl = buildCourierPortalWhatsAppLink({
      courierPhoneNumber: courier.phone_number,
      courierName: courier.name,
      storeName,
      portalUrl,
      locale: lang,
      t: t as unknown as TFunction,
    });
    if (!whatsappUrl) {
      toast.error(t('admin.couriers.errors.smsUnavailable' as TranslationKey));
      return;
    }
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  }

  function handleRevokePortalLink(courier: Courier): void {
    if (!storeId || !courier.active || courier.verification_status !== 'approved') return;
    revokePortalLink.mutate(courier.id);
  }

  function handleRegeneratePortalLink(courier: Courier): void {
    if (!storeId || !courier.active || courier.verification_status !== 'approved') return;
    regeneratePortalLink.mutate(courier.id);
  }

  function handleGeneratePortalLink(courier: Courier): void {
    if (!storeId || !courier.active || courier.verification_status !== 'approved') return;
    getOrCreatePortalLink.mutate(courier.id, {
      onSuccess: (result) => {
        if (result.success) {
          toast.success(t('admin.couriers.portal.generated' as TranslationKey));
        }
      },
    });
  }

  async function handleFormSubmit(values: CourierFormSubmit): Promise<void> {
    if (!storeId || !formMode) return;
    setFormError(null);
    try {
      if (formMode.kind === 'create') {
        await createCourier.mutateAsync({
          storeId,
          name: values.name,
          phoneNumber: values.phoneNumber,
          active: values.active,
          internalNotes: values.internalNotes,
        });
      } else {
        await updateCourier.mutateAsync({
          courierId: formMode.courier.id,
          storeId,
          name: values.name,
          phoneNumber: values.phoneNumber,
          active: values.active,
          internalNotes: values.internalNotes,
        });
      }
      setFormMode(null);
    } catch (err) {
      setFormError(
        (err as { key?: CourierErrorKey })?.key ?? 'admin.couriers.errors.networkFailed',
      );
    }
  }

  function handleToggleActive(courier: Courier): void {
    if (!storeId) return;
    updateCourier.mutate({
      courierId: courier.id,
      storeId,
      name: courier.name,
      phoneNumber: courier.phone_number,
      active: !courier.active,
      internalNotes: courier.internal_notes,
    });
  }

  function handleDelete(): void {
    if (!deleteTarget) return;
    setDeleteError(null);
    deleteCourier.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: (err) =>
        setDeleteError(
          (err as { key?: CourierErrorKey })?.key ?? 'admin.couriers.errors.networkFailed',
        ),
    });
  }

  if (isLoading) {
    return (
      <main
        dir={dir}
        data-testid="couriers-route"
        className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8"
      >
        <Skeleton className="mb-6 h-9 w-52" />
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </main>
    );
  }

  if (!canManageCouriers) {
    return (
      <main
        dir={dir}
        data-testid="couriers-route"
        className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 lg:px-8"
      >
        <div
          role="alert"
          className="border-border bg-card shadow-admin-card rounded-lg border p-8 text-center"
        >
          <AlertCircle className="text-destructive mx-auto mb-4 size-10" aria-hidden />
          <h1 className="font-display text-foreground mb-2 text-xl">
            {t('admin.couriers.errors.permissionDenied' as TranslationKey)}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t('common.errors.permissionDenied' as TranslationKey)}
          </p>
        </div>
      </main>
    );
  }

  const couriers = couriersQuery.data ?? [];
  const isEmpty = !couriersQuery.isLoading && couriers.length === 0;

  return (
    <main
      dir={dir}
      data-testid="couriers-route"
      className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8"
    >
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <h1 className="font-display text-foreground text-start text-[28px] font-bold leading-tight tracking-tight">
            {t('admin.couriers.title' as TranslationKey)}
          </h1>
          <p className="text-muted-foreground mt-1 text-start text-sm">
            {t('admin.couriers.count' as TranslationKey, { count: couriers.length })}
          </p>
        </div>
        <Button type="button" onClick={() => setFormMode({ kind: 'create' })}>
          <Plus className="size-4" aria-hidden />
          <span>{t('admin.couriers.actions.create' as TranslationKey)}</span>
        </Button>
      </div>

      {couriersQuery.isError ? (
        <div
          role="alert"
          className="border-border bg-card shadow-admin-card mx-auto max-w-lg rounded-lg border p-8 text-center"
        >
          <AlertCircle className="text-destructive mx-auto mb-4 size-10" aria-hidden />
          <h2 className="font-display text-foreground mb-2 text-xl">{t('common.error')}</h2>
          <p className="text-muted-foreground mb-5 text-sm">
            {t('admin.couriers.errors.networkFailed' as TranslationKey)}
          </p>
          <Button type="button" variant="outline" onClick={() => void couriersQuery.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {!couriersQuery.isError && isEmpty ? (
        <div
          data-testid="couriers-empty"
          className="border-border bg-card shadow-admin-card rounded-lg border p-10 text-center"
        >
          <h2 className="font-display text-foreground mb-2 text-xl">
            {t('admin.couriers.empty.title' as TranslationKey)}
          </h2>
          <p className="text-muted-foreground mx-auto mb-5 max-w-md text-sm">
            {t('admin.couriers.empty.description' as TranslationKey)}
          </p>
          <Button type="button" onClick={() => setFormMode({ kind: 'create' })}>
            <Plus className="size-4" aria-hidden />
            <span>{t('admin.couriers.actions.create' as TranslationKey)}</span>
          </Button>
        </div>
      ) : null}

      {!couriersQuery.isError && !isEmpty ? (
        <div className="space-y-6">
          <CouriersTable
            couriers={couriers}
            isMutating={isMutating}
            onEdit={(courier) => setFormMode({ kind: 'edit', courier })}
            onToggleActive={handleToggleActive}
            onDelete={setDeleteTarget}
            onVerify={setVerificationTarget}
            onCopyPortalLink={(courier) => void handleCopyPortalLink(courier)}
            onSharePortalLink={(courier) => void handleSharePortalLink(courier)}
            onSendPortalWhatsApp={(courier) => void handleSendPortalWhatsApp(courier)}
            onRevokePortalLink={handleRevokePortalLink}
            onRegeneratePortalLink={handleRegeneratePortalLink}
            onGeneratePortalLink={handleGeneratePortalLink}
            isPortalActionPending={isPortalActionPending}
          />
          <CouriersMobileCards
            couriers={couriers}
            isMutating={isMutating}
            onEdit={(courier) => setFormMode({ kind: 'edit', courier })}
            onToggleActive={handleToggleActive}
            onDelete={setDeleteTarget}
            onVerify={setVerificationTarget}
            onCopyPortalLink={(courier) => void handleCopyPortalLink(courier)}
            onSharePortalLink={(courier) => void handleSharePortalLink(courier)}
            onSendPortalWhatsApp={(courier) => void handleSendPortalWhatsApp(courier)}
            onRevokePortalLink={handleRevokePortalLink}
            onRegeneratePortalLink={handleRegeneratePortalLink}
            onGeneratePortalLink={handleGeneratePortalLink}
            isPortalActionPending={isPortalActionPending}
          />
        </div>
      ) : null}

      <CourierFormDialog
        open={!!formMode}
        courier={formMode?.kind === 'edit' ? formMode.courier : null}
        isSubmitting={createCourier.isPending || updateCourier.isPending}
        errorKey={formError}
        onOpenChange={(open) => {
          if (!open) {
            setFormMode(null);
            setFormError(null);
          }
        }}
        onSubmit={handleFormSubmit}
      />
      <DeleteCourierDialog
        open={!!deleteTarget}
        courier={deleteTarget}
        isDeleting={deleteCourier.isPending}
        errorKey={deleteError}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
        onConfirm={handleDelete}
      />
      <CourierVerificationDialog
        open={!!verificationTarget}
        storeId={storeId ?? ''}
        courier={verificationTarget}
        onOpenChange={(open) => {
          if (!open) setVerificationTarget(null);
        }}
      />
      <Dialog
        open={!!portalLinkRecovery}
        onOpenChange={(open) => {
          if (!open) setPortalLinkRecovery(null);
        }}
      >
        <DialogContent data-testid="courier-portal-link-recovery-dialog" dir={dir}>
          <DialogHeader>
            <DialogTitle>{t('admin.couriers.portal.recovery.title' as TranslationKey)}</DialogTitle>
            <DialogDescription>
              {t('admin.couriers.portal.recovery.description' as TranslationKey, {
                courierName: portalLinkRecovery?.courierName ?? '',
              })}
            </DialogDescription>
          </DialogHeader>
          <label className="text-muted-foreground text-start text-sm" htmlFor="portal-link-url">
            {t('admin.couriers.portal.recovery.linkLabel' as TranslationKey)}
          </label>
          <textarea
            id="portal-link-url"
            data-testid="courier-portal-link-recovery-url"
            readOnly
            value={portalLinkRecovery?.portalUrl ?? ''}
            onFocus={(event) => event.currentTarget.select()}
            className="border-input bg-background text-foreground focus-visible:ring-ring min-h-24 w-full resize-none rounded-md border px-3 py-2 text-start text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2"
          />
          <p className="text-muted-foreground text-start text-sm">
            {t('admin.couriers.portal.safetyNote' as TranslationKey)}
          </p>
          <DialogFooter>
            <Button type="button" onClick={() => void handleCopyRecoveredPortalLink()}>
              <Copy className="size-4" aria-hidden />
              <span>{t('admin.couriers.portal.copyLink' as TranslationKey)}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
